import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';
import { useTTS } from '../../hooks/useTTS';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Paper, Box, IconButton } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// removed unused theme import to avoid lint warnings
const PAGE_ID = 'm6_u1_x3_ec_ventilatoren';

const M6_U1_X3_EcVentilatoren: React.FC = () => {
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const textBlocks = useMemo(() => ([
    { key: 'intro', heading: 'EC-Ventilatoren: Effizienz und Präzision', body: 'EC-Ventilatoren (Electronically Commutated) sind bürstenlose DC-Motoren mit integrierter Elektronik, die direkt an das AC-Netz angeschlossen werden können. Sie kombinieren hohe Energieeffizienz mit exzellenter Regelbarkeit und setzen neue Maßstäbe in der Luftbewegungstechnik. Im Vergleich zu konventionellen AC-Motoren bieten sie bis zu 70% Energieeinsparung bei Teillast.' },
    { key: 'technical', heading: 'Technische Merkmale', body: 'EC-Ventilatoren zeichnen sich durch hohe Energieeffizienz (IE4/IE5), stufenlose Drehzahlregelung von 0-100%, kompakte Bauweise (integrierte Elektronik), lange Lebensdauer (über 80.000 Betriebsstunden), geringe Wartung (keine Bürsten), geringe Geräuschentwicklung, integrierte Steuerungsfunktionen, gutes Teillastverhalten und einfache Ansteuerung aus. Der elektronisch kommutierte Motor arbeitet hocheffizient über den gesamten Drehzahlbereich.' },
    { key: 'features', heading: 'Ansteuerung & Integration', body: 'EC-Ventilatoren können über 0-10V DC (Standard-Analogsignal), PWM-Signal, digitale Schnittstellen (Modbus RTU/TCP, BACnet MS/TP, BACnet IP), Potentiometer (manuelle Sollwertvorgabe), Festdrehzahlen (über Bedienelemente) oder interne Regelung auf Sensorwerte (Druck, Temperatur, CO₂) angesteuert werden. Die flexible Parametrierung ermöglicht Anpassung an spezifische Anwendungen.' },
    { key: 'advantages', heading: 'Hersteller & Auswahlkriterien', body: 'Typische Hersteller sind ebm-papst, Ziehl-Abegg, Rosenberg, Nicotra Gebhardt, Systemair, FläktGroup. Auswahlkriterien sind Volumenstrom und Druckdifferenz (Q/Δp), Ansteuerungssignal (0-10V, PWM, Bus), Versorgungsspannung (1~230V, 3~400V), Bauform (axial, radial, diagonal), Schutzart (IP20-IP68), Geräuschentwicklung, Zusatzfunktionen (Sensor-Integration, Alarmausgänge) und EMV-Konformität.' },
    { key: 'application', heading: 'Anwendung in der Praxis', body: 'EC-Ventilatoren finden Einsatz in Lüftungsgeräten (RLT-Anlagen, dezentrale Geräte, Klima-Splitgeräte), Kühl- und Kältetechnik, IT-Kühlung, Prozesslufttechnik und Absauganlagen. Die Auswahl erfolgt nach Volumenstrom/Druck-Kennlinie, Betriebspunkt im Wirkungsgradoptimum und Integration in Regelungssysteme. EC-Ventilatoren sind ideal für bedarfsgerechte Regelung (CO₂-gesteuert, präsenzbasiert, druckgesteuert) und ermöglichen erhebliche Energieeinsparungen in Lüftungsanlagen.' }
  ]), []);

  const images = [
    { key: 'ec_venti', src: '/assets/drawable/motor_ec_venti.png', label: 'EC-Ventilator mit integrierter Elektronik' }
  ];

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(
    () => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })),
    [override, textBlocks]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return;
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Speichern.');
    }
  }, [token, refresh]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return;
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Upload.');
    }
  }, [token, refresh]);

  const ttsBtn = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    color: 'primary',
    sx: { ml: 1 }
  }, React.createElement(VolumeUpIcon));

  return React.createElement(UnitPageShell, {
    title: '6.1.3 EC-Ventilatoren',
    actions: ttsBtn
  }, React.createElement(Paper, { elevation: 0, sx: { backgroundColor: 'transparent' } },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          label: img.label,
          onReplace: canUploadImages ? (file) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 } },
        mergedBlocks.map(block => React.createElement(EditableTextBlock, {
          key: block.key,
          pageId: PAGE_ID,
          blockId: block.key,
          heading: block.heading,
          body: block.body,
          onSave: canEditTexts ? (body: string) => handleTextSave(block.key, body) : undefined
        }))
      )
    )
  ));
};

export default M6_U1_X3_EcVentilatoren;


